import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import dayjs from "dayjs";
import { getLocale } from "react-i18nify";
import { useSnackbar } from "notistack";

import { FormControlLabel, Grid, Stack } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Divider from "@mui/material/Divider";

import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/EditOutlined";
import CircleIcon from "@mui/icons-material/Circle";
// import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SaveAltIcon from "@mui/icons-material/SaveAlt";

import { updateOrderNote } from "src/api/purchaseOrder";
import { getCancelOrderDetail } from "src/api/cancelOrder";

import { checkNotEmpty, convertIntToCurrency } from "src/common";
import { device } from "src/styles/device";
import font from "src/styles/font";
import { DarkGrey, Primary } from "src/styles/color";
import useDeviceSize from "src/hooks/useDeviceSize";
import { useAuth } from "src/hooks/useAuth";
import { useDialog } from "src/context/DialogContext";

import Main from "src/components/ui/Container";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import Checkbox from "src/components/core/Checkbox";
import Loading from "src/components/ui/Loading";
import {
  Container,
  DivBackground,
  DivPaymentContainer,
  DivPaymentTitle,
  DivProductTitle,
  DownloadDocBox,
  FocusRowSumPrice,
  HeaderFixed,
  HeaderItem,
  ImgSlip,
  Price,
  RowDownloadDoc,
  RowSumPrice,
  SelectSpan,
  SpanContent,
  SpanPaymentStatus,
  SpanPaymentType,
  SpanSubHeader,
  SpanSubTitle,
  SpanTitle,
  StyledButton,
  StyledMenuItem,
  StyledSelect,
  WrapperAttachPayment,
  WrapperCalOrder,
  WrapperHeaderFixed,
  WrapperImgSlip,
  WrapperProductCondition,
  WrapperTextField,
  WrapperTextNote,
} from "src/components/order/components";

import PreviewDialog from "src/modules/promo-code/PreviewDialog";
import CancelDialog from "src/modules/order/purchase-order/modal/CancelDialog";
import EditDocumentDialog from "src/modules/order/purchase-order/modal/EditDocumentDialog";
import RefundSuccessDialog from "src/modules/order/cancel-refund/modal/RefundSuccessDialog";
import EditRefundDialog from "src/modules/order/cancel-refund/modal/EditRefundDialog";
import ProductOrderTable from "src/components/order/components/ProductOrderTable";
// import OrderHistoryDialog from "src/modules/order/purchase-order/modal/OrderHistoryDialog";

const SpanCancelText = styled.span`
  color: ${DarkGrey};
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 24px;
  /* margin-bottom: 16px; */

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanRemark = styled.div`
  color: ${DarkGrey};
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;
  /* margin-bottom: 8px; */

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const RefundRowSumPrice = styled(FocusRowSumPrice)`
  margin-top: 0;
`;

function CancelDetail({ open }) {
  const { orderId } = useParams();
  const { checkAllowPermission } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const { openDialog, closeDialog, closeAllDialogs } = useDialog();
  const [deviceWidth] = useDeviceSize();

  const initialUpdate = {
    _id: orderId,
    status: {
      value: "",
      cancelReason: "",
    },
    internalNote: "",
  };
  const [updateOrder, setUpdateOrder] = useState(initialUpdate);
  const [orderDetail, setOrderDetail] = useState({});

  const [isLoading, setIsLoading] = useState(false);

  const statusList = [
    { label: "ยกเลิก", value: "cancel", color: "#BCBCC0", showingStatus: [] },
    {
      label: "คืนเงิน",
      value: "waiting refund",
      color: "#BF0013",
      showingStatus: ["refunded"],
    },
    {
      label: "คืนเงินสำเร็จ",
      value: "refunded",
      color: "#219653",
      showingStatus: [],
    },
  ];

  const paymentTypes = [
    { label: "Promptpay", value: "promptpay" },
    { label: "โอนบัญชี XSURFACE", value: "transfer" },
    { label: "บัตรเครดิต / เดบิต", value: "credit-card" },
  ];

  useEffect(() => {
    if (orderId === undefined) return;

    fetchOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);
      await getCancelOrderDetail(orderId)
        .then(async ({ data: { data } }) => {
          setOrderDetail({
            ...data,
            status: data?.status,
            internalNote: data?.order?.internalNote || "",
          });
          setUpdateOrder({
            ...data,
            status: data?.status,
            internalNote: data?.order?.internalNote || "",
          });
        })
        .catch((err) => {
          console.error("err", err);
          // navigate("/404");
        });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateInternalNote = async () => {
    try {
      setIsLoading(true);
      const updateParams = { internalNote: updateOrder?.internalNote };

      await updateOrderNote(orderId, updateParams);
      enqueueSnackbar(`Update Note Success!`, { variant: "success" });
      await fetchOrderDetail();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatus = (e) => {
    let selectedStatus = e.target.value;

    if (selectedStatus === "cancel") {
      openDialog({
        type: "custom",
        customDialog: (
          <CancelDialog
            orderId={orderDetail?._id}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    } else if (selectedStatus === "refunded") {
      openDialog({
        type: "custom",
        customDialog: (
          <RefundSuccessDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    }
  };

  const telFormat = (tel) => {
    if (!checkNotEmpty(tel)) return;
    let new_tel = tel.slice(0, 3) + "-" + tel.slice(3, 6) + "-" + tel.slice(6);
    return new_tel;
  };

  const convertPrice = (price) =>
    Intl.NumberFormat("th-TH", { style: "currency", currency: "THB" }).format(
      price,
    );

  useEffect(() => {
    if (isLoading) document.body.style.overflow = "hidden";
    else document.body.style = "none";
  }, [isLoading]);

  const PriceSummary = ({ summary }) => {
    return (
      <WrapperCalOrder>
        <RowSumPrice>
          <Price desc>
            {getLocale() === "th" ? "ราคาสินค้า" : "Product price"}
          </Price>
          <Price>
            {convertIntToCurrency(
              orderDetail?.order?.totalPriceOfProduct ||
                orderDetail?.order?.productChoiceOrders
                  ?.map((product) => product.price * product.volume)
                  .reduce((a, b) => a + b, 0),
            )}{" "}
            บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>
            {getLocale() === "th" ? "ราคาช่าง" : "Service price"}

            {!!orderDetail?.order?.totalInstaller && (
              <span>*ช่างประกอบและติดตั้ง</span>
            )}
          </Price>
          <Price>
            {!!orderDetail?.order?.totalInstaller
              ? convertIntToCurrency(orderDetail?.order?.totalInstaller)
              : convertIntToCurrency(
                  orderDetail?.order?.totalPriceOfService ||
                    orderDetail?.order?.productChoiceOrders
                      ?.filter(
                        (product) =>
                          product?.installDetailGroup?.isService === true,
                      )
                      ?.map(
                        (product) =>
                          (product?.productChoiceOrder?.group_price
                            ?.install_price || 0) *
                            product?.installDetailGroup?.installArea +
                          product?.installDetailGroup?.servicePrice,
                      )
                      .reduce((a, b) => a + b, 0) ||
                    0,
                )}{" "}
            บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>
            {!!orderDetail?.order?.discount?.promoCodeRef?._id
              ? getLocale() === "th"
                ? "คูปองส่วนลด"
                : "Discount coupon"
              : getLocale() === "th"
                ? "ส่วนลด"
                : "Discount"}
            {!!orderDetail?.order?.discount?.promoCodeRef?._id && (
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  openDialog({
                    type: "custom",
                    customDialog: (
                      <PreviewDialog
                        isDetail
                        previewData={{
                          ...orderDetail?.order?.discount?.promoCodeRef,
                          promo_code:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoCode,
                          promo_name:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoName,
                          promo_title:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoTitle,
                          promo_start_time:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoStartTime,
                          promo_expire_time:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoExpireTime,
                          promo_pay_type:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoPayType,
                          promo_description:
                            orderDetail?.order?.discount?.promoCodeRef
                              ?.promoDescription,
                        }}
                        onClose={() => closeDialog()}
                      />
                    ),
                  });
                }}
              >
                {orderDetail?.order?.discount?.promoCodeRef?.promoCode}
              </span>
            )}
          </Price>
          <Price>
            {convertIntToCurrency(
              orderDetail?.order?.productChoiceOrders
                ?.map((product) => product?.discount)
                .reduce((a, b) => a + b, 0) || 0,
            )}{" "}
            บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>{getLocale() === "th" ? "รวม" : "Subtotal"}</Price>
          <Price>
            {convertIntToCurrency(orderDetail?.order?.totalPrice)} บาท
          </Price>
        </RowSumPrice>

        <RowSumPrice>
          <Price desc>{getLocale() === "th" ? "ภาษี 7%" : "Vat 7%"}</Price>
          <Price>{convertIntToCurrency(orderDetail?.order?.vat)} บาท</Price>
        </RowSumPrice>

        {orderDetail?.order?.statusPayment?.paymentType === "credit-card" && (
          <RowSumPrice>
            <Price desc>
              {getLocale() === "th" ? "ค่าธรรมเนียม" : "Credit card fee"}
            </Price>
            <Price>
              {convertIntToCurrency(
                orderDetail?.order?.statusPayment?.depositPrice
                  ? orderDetail?.order?.totalAll * 0.03
                  : 0,
              )}{" "}
              บาท
            </Price>
          </RowSumPrice>
        )}

        {summary && (
          <RowSumPrice>
            <Price desc>
              {getLocale() === "th" ? "ราคารวมทั้งหมด" : "Grand total"}
            </Price>
            <Price>
              {orderDetail?.order?.statusPayment?.paymentType === "credit-card"
                ? convertIntToCurrency(orderDetail?.order?.totalAll * 1.03)
                : convertIntToCurrency(orderDetail?.order?.totalVat)}{" "}
              บาท
            </Price>
          </RowSumPrice>
        )}
      </WrapperCalOrder>
    );
  };

  return (
    <>
      <Main open={open}>
        <WrapperHeaderFixed>
          <HeaderFixed>
            <HeaderItem>
              <SpanTitle>
                {getLocale() === "th" ? "รายละเอียดใบสั่งซื้อ" : "Order detail"}
              </SpanTitle>
            </HeaderItem>

            <HeaderItem>
              <FormControl sx={{ minWidth: 120, alignSelf: "flex-end" }}>
                <StyledSelect
                  labelId="status-select-label"
                  id="status-select"
                  value={updateOrder?.status?.value}
                  disabled={
                    !checkAllowPermission("orders.can_update_cancelOrder") ||
                    orderDetail?.status?.value === "cancel" ||
                    orderDetail?.status?.value === "refunded"
                  }
                  onChange={handleStatus}
                  MenuProps={{
                    PaperProps: {
                      sx: { borderRadius: 4, ".MuiList-root": { padding: 0 } },
                    },
                  }}
                >
                  {statusList.map((option) => {
                    let currentStatus = statusList.find(
                      ({ value }) => value === orderDetail?.status?.value,
                    );

                    return (
                      <StyledMenuItem
                        value={option.value}
                        style={
                          !currentStatus?.showingStatus?.includes(option.value)
                            ? { display: "none" }
                            : {}
                        }
                      >
                        <Stack gap={1} direction="row" alignItems="center">
                          <CircleIcon
                            fontSize={font.size.sm}
                            sx={{ color: option.color }}
                          />
                          <SelectSpan>{option.label}</SelectSpan>
                        </Stack>
                      </StyledMenuItem>
                    );
                  })}
                </StyledSelect>
              </FormControl>
            </HeaderItem>
          </HeaderFixed>
        </WrapperHeaderFixed>

        <Container>
          <Grid container spacing={2}>
            {/* Customer/Order Detail */}
            <Grid item xs={12} sx={{ margin: "0 auto" }}>
              <DivBackground>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12}>
                    <SpanTitle>
                      {getLocale() === "th" ? "ข้อมูลลูกค้า" : "ข้อมูลลูกค้า"}
                    </SpanTitle>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>
                      {getLocale() === "th"
                        ? "หมายเลขคำสั่งซื้อ"
                        : "Order Number"}
                    </SpanSubHeader>
                    <SpanContent>
                      <Link
                        to={`/order/detail/${orderDetail?.order?._id}`}
                        target="_blank"
                      >
                        {orderDetail?.order?.orderNumber}
                      </Link>
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>
                      {getLocale() === "th" ? "หมายเลขใบเสนอราคา" : "QT Number"}
                    </SpanSubHeader>
                    <SpanContent>
                      {orderDetail?.order?.qtNumber || "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ชื่อลูกค้า</SpanSubHeader>
                    <SpanContent>
                      {orderDetail?.order?.shippingAddress?.name}
                    </SpanContent>
                  </Grid>

                  {/* <Grid item xs={12} sm={6}>
                    <SpanSubHeader>เลขประจำตัวผู้เสียภาษี</SpanSubHeader>
                    <SpanContent>
                      {orderDetail?.order?.taxInvoiceAddress?.taxId
                        ? orderDetail?.order?.taxInvoiceAddress?.taxId
                        : "-"}
                    </SpanContent>
                  </Grid> */}

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>อีเมล</SpanSubHeader>
                    <SpanContent>{orderDetail?.order?.user?.email}</SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>เบอร์โทรศัพท์</SpanSubHeader>
                    <SpanContent>
                      {telFormat(orderDetail?.order?.shippingAddress?.tel) ||
                        "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ที่อยู่</SpanSubHeader>
                    <SpanContent>
                      {`
                      ${orderDetail?.order?.shippingAddress?.address || ""} ${
                        orderDetail?.order?.shippingAddress?.subdistrict || ""
                      } 
                      ${orderDetail?.order?.shippingAddress?.district || ""} ${
                        orderDetail?.order?.shippingAddress?.province || ""
                      } 
                      ${orderDetail?.order?.shippingAddress?.zipcode || ""}
                      `}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ออเดอร์อ้างอิง</SpanSubHeader>
                    <SpanContent>
                      {!!orderDetail?.order?.refDraftOrder ? (
                        <Link
                          to={`/sale-order/detail/${orderDetail?.order?.refDraftOrder?._id}`}
                          target="_blank"
                        >
                          {orderDetail?.order?.refDraftOrder?.running_number}
                        </Link>
                      ) : (
                        ` - `
                      )}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <SpanSubHeader>วันที่สั่งซื้อ</SpanSubHeader>
                    <SpanContent>
                      {dayjs(orderDetail?.order?.createdAt).format(
                        "DD-MM-YYYY",
                      )}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <SpanSubHeader>จัดส่งภายในวันที่</SpanSubHeader>
                    <SpanContent>
                      {!!orderDetail?.order?.scheduleDate
                        ? dayjs(orderDetail?.order?.scheduleDate).format(
                            "DD-MM-YYYY",
                          )
                        : "-"}
                    </SpanContent>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      label="ใบกำกับภาษี"
                      sx={{
                        margin: 0,
                        ".MuiFormControlLabel-label": {
                          fontSize: "1rem",
                          fontWeight: 400,
                          lineHeight: "24px",
                        },
                        ".MuiCheckbox-root": { p: 0.5, mr: 0.5 },
                      }}
                      control={
                        <Checkbox
                          name="install-price-checkbox"
                          checked={
                            checkNotEmpty(
                              orderDetail?.order?.taxInvoiceAddress?.name,
                            )
                              ? true
                              : false
                          }
                          disabled={orderDetail?.order?.status === "ordered"}
                          // onChange={(e) => {
                          //   onChangeUpload(
                          //     null,
                          //     `choices.${choiceIndex}.pre_order_date`,
                          //     "7 - 14  วัน",
                          //   );
                          // }}
                        />
                      }
                    />
                  </Grid>

                  {checkNotEmpty(
                    orderDetail?.order?.taxInvoiceAddress?.name,
                  ) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12}>
                        <SpanTitle>
                          {getLocale() === "th"
                            ? "ข้อมูลใบกำกับภาษี"
                            : "ข้อมูลใบกำกับภาษี"}
                        </SpanTitle>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>ชื่อ</SpanSubHeader>
                        <SpanContent>
                          {orderDetail?.order?.taxInvoiceAddress?.name}
                        </SpanContent>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>เลขประจำตัวผู้เสียภาษี</SpanSubHeader>
                        <SpanContent>
                          {orderDetail?.order?.taxInvoiceAddress?.taxId
                            ? orderDetail?.order?.taxInvoiceAddress?.taxId
                            : "-"}
                        </SpanContent>
                      </Grid>

                      {orderDetail?.order?.taxInvoiceAddress?.type ===
                        "company" &&
                      orderDetail?.order?.taxInvoiceAddress?.companyName !==
                        "" ? (
                        <>
                          <Grid item xs={6}>
                            <SpanSubHeader>ชื่อบริษัท</SpanSubHeader>
                            <SpanContent>
                              {orderDetail?.order?.taxInvoiceAddress
                                ?.companyName
                                ? orderDetail?.order?.taxInvoiceAddress
                                    ?.companyName
                                : "-"}
                            </SpanContent>
                          </Grid>

                          <Grid item xs={6}>
                            <SpanSubHeader>สาขา</SpanSubHeader>
                            <SpanContent>
                              {orderDetail?.order?.taxInvoiceAddress?.branch
                                ? orderDetail?.order?.taxInvoiceAddress?.branch
                                : "-"}
                            </SpanContent>
                          </Grid>
                        </>
                      ) : null}

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>เบอร์โทรศัพท์</SpanSubHeader>
                        <SpanContent>
                          {orderDetail?.order?.taxInvoiceAddress?.tel}
                        </SpanContent>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <SpanSubHeader>ที่อยู่จัดส่ง</SpanSubHeader>
                        <SpanContent>
                          {`${orderDetail?.order?.taxInvoiceAddress?.shippingAddress?.address || ""} ${
                            orderDetail?.order?.taxInvoiceAddress
                              ?.shippingAddress?.subdistrict || ""
                          } 
                            ${orderDetail?.order?.taxInvoiceAddress?.shippingAddress?.district || ""} ${
                              orderDetail?.order?.taxInvoiceAddress
                                ?.shippingAddress?.province || ""
                            } 
                            ${orderDetail?.order?.taxInvoiceAddress?.shippingAddress?.zipcode || ""}
                          `}
                        </SpanContent>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DivBackground>
            </Grid>

            {/* Order summary Section */}
            <Grid item xs={12}>
              <DivBackground>
                <Grid container spacing={3}>
                  {/* Order Product List */}
                  <Grid
                    item
                    container
                    xs={12}
                    spacing={2}
                    sx={{ alignItems: "center" }}
                  >
                    <Grid item xs={12}>
                      <DivProductTitle>
                        <SpanTitle>ข้อมูลสินค้า</SpanTitle>

                        <SpanCancelText>
                          ยกเลิก วันที่{" "}
                          {dayjs(orderDetail?.createdAt).format("DD-MM-YYYY")}
                        </SpanCancelText>
                      </DivProductTitle>
                    </Grid>

                    <Grid item sm={5} xs={12}>
                      <SpanSubTitle>
                        ร้านค้า {orderDetail?.order?.sellerName}
                      </SpanSubTitle>
                    </Grid>

                    <Grid item xs={12}>
                      <ProductOrderTable
                        productOrders={orderDetail?.order?.productChoiceOrders}
                      />
                    </Grid>
                  </Grid>

                  {/* Order summary */}
                  <Grid item container xs={12} spacing={3}>
                    {/* Product condition */}
                    <Grid item sm={6} xs={12}>
                      <SpanTitle>เงื่อนไขสินค้า</SpanTitle>

                      <WrapperProductCondition>
                        <TextField
                          className="TextField-without-border-radius"
                          placeholder="เงื่อนไขสินค้า"
                          fullWidth
                          // disabled={orderDetail?.order?.status === "ordered"}
                          multiline
                          minRows={
                            deviceWidth > 835 ? 12 : deviceWidth <= 540 ? 3 : 10
                          }
                          maxRows={deviceWidth > 540 ? 15 : 7}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ readOnly: true }}
                          value={orderDetail?.order?.condition || ""}
                        />
                      </WrapperProductCondition>
                    </Grid>

                    {/* Order Price Summary */}
                    <Grid item sm={6} xs={12}>
                      <SpanTitle color={Primary}>
                        {getLocale() === "th"
                          ? "สรุปใบสั่งซื้อ"
                          : "Purchase order summary"}
                      </SpanTitle>

                      <PriceSummary summary />
                    </Grid>
                  </Grid>

                  {/* Payment Section */}
                  {((orderDetail?.order?.statusPayment?.paymentType !==
                    "credit-card" &&
                    !!orderDetail?.order?.statusPayment?.paySlipImage) ||
                    (orderDetail?.order?.statusPayment?.paymentType ===
                      "credit-card" &&
                      orderDetail?.order?.statusPayment?.status ===
                        "paid")) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12}>
                        <DivPaymentContainer
                          single={
                            !!orderDetail?.order?.statusPaymentRePay
                              ?.paySlipOtherImage?._id
                              ? false
                              : true
                          }
                        >
                          {/* payment section */}
                          <Grid container spacing={2}>
                            {/* payment slip */}
                            <Grid item xs={12}>
                              <SpanTitle>การชำระเงิน</SpanTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  {getLocale() === "th"
                                    ? "ชำระเงิน"
                                    : "Payment slip"}
                                </SpanPaymentType>

                                {orderDetail?.order?.statusPayment?.status ===
                                "paid" ? (
                                  <SpanPaymentStatus>
                                    จ่ายเต็มจำนวน
                                  </SpanPaymentStatus>
                                ) : orderDetail?.order?.statusPayment
                                    ?.status === "deposit" ? (
                                  <SpanPaymentStatus>
                                    มัดจำ{" "}
                                    <span>
                                      {`(
                                  ${
                                    orderDetail?.order?.statusPayment
                                      ?.depositPrice
                                      ? convertPrice(
                                          orderDetail?.order?.statusPayment
                                            ?.depositPrice,
                                          orderDetail?.order?.statusPayment
                                            ?.depositPrice,
                                        )
                                      : 0
                                  }
                                  )`}
                                    </span>
                                    {/* มัดจำ <span>(5,000)</span> */}
                                  </SpanPaymentStatus>
                                ) : null}
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  ช่องทางการชำระเงิน
                                </SpanPaymentType>

                                <SpanPaymentStatus>
                                  {paymentTypes.find(
                                    (type) =>
                                      orderDetail?.order?.statusPayment
                                        ?.paymentType === type.value,
                                  )?.label || "-"}
                                </SpanPaymentStatus>
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  วันที่ชำระเงิน
                                </SpanPaymentType>

                                <SpanPaymentStatus>
                                  {!!orderDetail?.order?.statusPayment?.slipDate
                                    ? dayjs(
                                        orderDetail?.order?.statusPayment
                                          ?.slipDate,
                                      ).format("DD-MM-YYYY")
                                    : "-"}
                                </SpanPaymentStatus>
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <WrapperTextField>
                                <TextField
                                  className="TextField-without-border-radius"
                                  label="Ref Pay NO."
                                  InputLabelProps={{ shrink: true }}
                                  InputProps={{ readOnly: true }}
                                  sx={{
                                    margin: "auto",
                                    width: "100%",
                                    textAlign: "center",
                                  }}
                                  value={
                                    orderDetail?.order?.statusPayment
                                      ?.refPayNo || ""
                                  }
                                />
                              </WrapperTextField>
                            </Grid>

                            {!!orderDetail?.order?.statusPayment
                              ?.paySlipImage && (
                              <Grid item xs={12}>
                                <RowDownloadDoc>
                                  <WrapperAttachPayment>
                                    <WrapperImgSlip>
                                      <ImgSlip
                                        src={
                                          orderDetail?.order?.statusPayment
                                            ?.paySlipImage?.docUrl
                                        }
                                        alt="paySlipImage"
                                      />
                                    </WrapperImgSlip>

                                    <Button
                                      onClick={() => {
                                        window.open(
                                          `${orderDetail?.order?.statusPayment?.paySlipImage?.docUrl}`,
                                          "_blank",
                                        );
                                      }}
                                      sx={{
                                        padding: "8px 16px",
                                        fontSize: font.size.sm,
                                        "&.MuiButton-outlined": {
                                          border: "1px solid #d9d9d9",
                                        },
                                      }}
                                      variant="outlined"
                                      startIcon={<SaveAltIcon />}
                                    >
                                      {getLocale() === "th"
                                        ? "ดาวน์โหลด"
                                        : "Download"}
                                    </Button>
                                  </WrapperAttachPayment>
                                </RowDownloadDoc>
                              </Grid>
                            )}
                          </Grid>

                          {!!orderDetail?.order?.statusPaymentRePay
                            ?.paySlipOtherImage && (
                            <>
                              {/* desktop */}
                              <Divider
                                orientation="vertical"
                                sx={{ display: { md: "initial", xs: "none" } }}
                              />

                              {/* mobile/tablet */}
                              <Divider sx={{ display: { md: "none" } }} />

                              {/* payment section */}
                              <Grid container spacing={2}>
                                {/* payment other slip */}
                                <Grid item xs={12}>
                                  <SpanTitle>การชำระเงิน</SpanTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <DivPaymentTitle>
                                    <SpanPaymentType>
                                      {getLocale() === "th"
                                        ? "ชำระเงิน"
                                        : "Payment slip"}
                                    </SpanPaymentType>

                                    {orderDetail?.order?.statusPaymentRePay
                                      ?.status === "paid" && (
                                      <SpanPaymentStatus>
                                        จ่ายเต็มจำนวน
                                      </SpanPaymentStatus>
                                    )}
                                  </DivPaymentTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <DivPaymentTitle>
                                    <SpanPaymentType>
                                      ช่องทางการชำระเงิน
                                    </SpanPaymentType>

                                    <SpanPaymentStatus>
                                      {paymentTypes.find(
                                        (type) =>
                                          orderDetail?.order?.statusPaymentRePay
                                            ?.paymentType === type.value,
                                      )?.label || "-"}
                                    </SpanPaymentStatus>
                                  </DivPaymentTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <DivPaymentTitle>
                                    <SpanPaymentType>
                                      วันที่ชำระเงิน
                                    </SpanPaymentType>

                                    <SpanPaymentStatus>
                                      {!!orderDetail?.order?.statusPaymentRePay
                                        ?.slipOtherDate
                                        ? dayjs(
                                            orderDetail?.order
                                              ?.statusPaymentRePay
                                              ?.slipOtherDate,
                                          ).format("DD-MM-YYYY")
                                        : "-"}
                                    </SpanPaymentStatus>
                                  </DivPaymentTitle>
                                </Grid>

                                <Grid item xs={12}>
                                  <WrapperTextField>
                                    <TextField
                                      className="TextField-without-border-radius"
                                      label="Ref Pay NO."
                                      InputLabelProps={{ shrink: true }}
                                      InputProps={{ readOnly: true }}
                                      sx={{
                                        margin: "auto",
                                        width: "100%",
                                        textAlign: "center",
                                      }}
                                      value={
                                        orderDetail?.order?.statusPaymentRePay
                                          ?.refPayNo || ""
                                      }
                                    />
                                  </WrapperTextField>
                                </Grid>

                                <Grid item xs={12}>
                                  <RowDownloadDoc>
                                    <WrapperAttachPayment>
                                      <WrapperImgSlip>
                                        <ImgSlip
                                          src={
                                            orderDetail?.order
                                              ?.statusPaymentRePay
                                              ?.paySlipOtherImage?.docUrl
                                          }
                                          alt="paySlipOtherImage"
                                        />
                                      </WrapperImgSlip>

                                      <Button
                                        onClick={() => {
                                          window.open(
                                            `${orderDetail?.order?.statusPaymentRePay?.paySlipOtherImage?.docUrl}`,
                                            "_blank",
                                          );
                                        }}
                                        sx={{
                                          padding: "8px 16px",
                                          fontSize: font.size.sm,
                                          "&.MuiButton-outlined": {
                                            border: "1px solid #d9d9d9",
                                          },
                                        }}
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                      >
                                        {getLocale() === "th"
                                          ? "ดาวน์โหลด"
                                          : "Download"}
                                      </Button>
                                    </WrapperAttachPayment>
                                  </RowDownloadDoc>
                                </Grid>
                              </Grid>
                            </>
                          )}
                        </DivPaymentContainer>
                      </Grid>
                    </>
                  )}

                  {!!orderDetail?.order?.receiptPdf && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      {/* Deposit receipt */}
                      <Grid item container xs={12} spacing={3}>
                        <Grid item container sm={6} xs={12} spacing={1}>
                          <Grid item xs={12}>
                            <SpanTitle>ใบเสร็จรับเงินชั่วคราว</SpanTitle>
                          </Grid>

                          <Grid item xs={12}>
                            <DownloadDocBox single>
                              <StyledButton
                                fullWidth
                                onClick={() => {
                                  window.open(
                                    `${orderDetail?.order?.receiptPdf?.docUrl}`,
                                    "_blank",
                                  );
                                }}
                                variant="outlined"
                                color="secondary"
                                startIcon={<AttachFileIcon />}
                              >
                                ใบเสร็จรับเงินชั่วคราว
                              </StyledButton>
                            </DownloadDocBox>
                          </Grid>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </DivBackground>
            </Grid>

            {/* Download receipt */}
            {!!orderDetail?.order?.quotationPdf && (
              <Grid item xs={12}>
                <DivBackground>
                  <SpanTitle mb={8}>เอกสารสำหรับลูกค้า</SpanTitle>

                  {/* Customer document */}
                  <Grid container spacing={3}>
                    <Grid item container sm={6} xs={12} spacing={1}>
                      <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" gap={2}>
                          <SpanSubTitle>ใบเสนอราคา</SpanSubTitle>

                          {checkAllowPermission(
                            "orders.can_update_cancelOrder",
                          ) &&
                            orderDetail?.status?.value !== "refunded" && (
                              <Button
                                sx={{ px: 2, py: 1 }}
                                onClick={() =>
                                  openDialog({
                                    type: "custom",
                                    customDialog: (
                                      <EditDocumentDialog
                                        orderId={orderDetail?.order?._id}
                                        quoData={{
                                          qtNumber:
                                            orderDetail?.order?.qtNumber || "",
                                          quotationPdf:
                                            orderDetail?.order?.quotationPdf ||
                                            "",
                                          quotationOtherPdf:
                                            orderDetail?.order
                                              ?.quotationOtherPdf || "",
                                        }}
                                        onClose={() => closeDialog()}
                                        onSuccess={() => {
                                          closeAllDialogs();
                                          fetchOrderDetail();
                                        }}
                                      />
                                    ),
                                  })
                                }
                                startIcon={
                                  <EditIcon
                                    sx={{
                                      color: Primary,
                                      fontSize: font.size.md,
                                    }}
                                  />
                                }
                              >
                                แก้ไข
                              </Button>
                            )}
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <DownloadDocBox
                          single={
                            !!orderDetail?.order?.quotationOtherPdf
                              ? false
                              : true
                          }
                        >
                          <StyledButton
                            fullWidth
                            onClick={() => {
                              window.open(
                                `${orderDetail?.order?.quotationPdf?.docUrl}`,
                                "_blank",
                              );
                            }}
                            variant="outlined"
                            color="secondary"
                            startIcon={<AttachFileIcon />}
                          >
                            ใบเสนอราคา
                          </StyledButton>

                          {!!orderDetail?.order?.quotationOtherPdf && (
                            <StyledButton
                              fullWidth
                              onClick={() => {
                                window.open(
                                  `${orderDetail?.order?.quotationOtherPdf?.docUrl}`,
                                  "_blank",
                                );
                              }}
                              variant="outlined"
                              color="secondary"
                              startIcon={<AttachFileIcon />}
                            >
                              เพิ่มเติม
                            </StyledButton>
                          )}
                        </DownloadDocBox>
                      </Grid>
                    </Grid>
                  </Grid>
                </DivBackground>
              </Grid>
            )}

            {/* Cancellation reason */}
            <Grid item xs={12}>
              <DivBackground>
                <SpanTitle color={Primary}>
                  {getLocale() === "th"
                    ? "สาเหตุที่ยกเลิก"
                    : "Cancellation reason"}
                </SpanTitle>

                <WrapperTextNote>
                  <TextField
                    id="cancellation-reason-input"
                    fullWidth
                    multiline
                    rows={2}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                    value={orderDetail?.status?.cancelReason || ""}
                    placeholder={
                      getLocale() === "th"
                        ? "สาเหตุที่ยกเลิก"
                        : "Cancellation reason"
                    }
                  />
                </WrapperTextNote>
              </DivBackground>
            </Grid>

            {/* Refund section */}
            {(orderDetail?.status?.value === "waiting refund" ||
              orderDetail?.status?.value === "refunded") && (
              <Grid item xs={12}>
                <DivBackground>
                  <Grid container spacing={3}>
                    {/* order summary */}
                    <Grid
                      item
                      xs={12}
                      container
                      alignItems="center"
                      columnSpacing={2}
                      rowSpacing={{ sm: 2, xs: 1 }}
                    >
                      <Grid item xs>
                        <SpanTitle color={Primary}>
                          {getLocale() === "th" ? "การคืนเงิน" : "Refund"}
                        </SpanTitle>
                      </Grid>

                      {/* Edit total refund & remark */}
                      {checkAllowPermission("orders.can_update_cancelOrder") &&
                        orderDetail?.status?.value !== "refunded" && (
                          <Grid item xs="auto">
                            <Button
                              variant="outlined"
                              onClick={() => {
                                openDialog({
                                  type: "custom",
                                  customDialog: (
                                    <EditRefundDialog
                                      orderDetail={orderDetail}
                                      onClose={() => closeDialog()}
                                      onSuccess={() => {
                                        closeAllDialogs();
                                        fetchOrderDetail();
                                      }}
                                      setIsLoading={setIsLoading}
                                    />
                                  ),
                                });
                              }}
                              sx={{ minWidth: 125 }}
                              startIcon={
                                <EditIcon
                                  sx={{
                                    color: Primary,
                                    fontSize: font.size.md,
                                  }}
                                />
                              }
                            >
                              {getLocale() === "th"
                                ? "แก้ไขจำนวนเงิน"
                                : "Edit price"}
                            </Button>
                          </Grid>
                        )}

                      <Grid item xs={12}>
                        <RefundRowSumPrice>
                          <Price desc bold>
                            {getLocale() === "th"
                              ? "จำนวนเงินที่คืน"
                              : "Total refund"}
                          </Price>
                          <Price focus>
                            <span>
                              {convertIntToCurrency(
                                orderDetail?.payment?.total,
                              )}
                            </span>{" "}
                            บาท
                          </Price>
                        </RefundRowSumPrice>
                      </Grid>

                      <Grid item xs={12}>
                        <SpanTitle>
                          {getLocale() === "th" ? "หมายเหตุ" : "Remark"}
                        </SpanTitle>

                        <WrapperTextNote>
                          <TextField
                            id="remark-reason-input"
                            fullWidth
                            multiline
                            rows={2}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ readOnly: true }}
                            value={orderDetail?.payment?.remark || ""}
                            placeholder={
                              getLocale() === "th" ? "หมายเหตุ" : "Remark"
                            }
                          />
                        </WrapperTextNote>
                      </Grid>
                    </Grid>

                    {/* Refund slip */}
                    {orderDetail?.status?.value === "refunded" &&
                      !!orderDetail?.payment?.paymentProof && (
                        <>
                          <Grid item xs={12}>
                            <Divider />
                          </Grid>

                          <Grid item xs={12} container spacing={2}>
                            <Grid item xs={12}>
                              <SpanTitle>หลักฐานการคืนเงิน</SpanTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <SpanRemark>
                                {`- ชื่อบัญชีที่รับเงินคืนจะต้องเป็นชื่อเดียวกับชื่อที่โอนเข้าบริษัทฯเท่านั้น`}
                              </SpanRemark>
                              <SpanRemark>
                                {`- ใช้เวลาดำเนินการ 7-14 วันทำการ`}
                              </SpanRemark>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  ช่องทางการชำระเงิน
                                </SpanPaymentType>

                                <SpanPaymentStatus>
                                  {paymentTypes.find(
                                    (type) =>
                                      orderDetail?.payment?.method ===
                                      type.value,
                                  )?.label || "-"}
                                </SpanPaymentStatus>
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <DivPaymentTitle>
                                <SpanPaymentType>
                                  วันที่ชำระเงิน
                                </SpanPaymentType>

                                <SpanPaymentStatus>
                                  {!!orderDetail?.payment?.method
                                    ? dayjs(
                                        orderDetail?.payment?.paymentDate,
                                      ).format("DD-MM-YYYY")
                                    : "-"}
                                </SpanPaymentStatus>
                              </DivPaymentTitle>
                            </Grid>

                            <Grid item xs={12}>
                              <RowDownloadDoc>
                                <WrapperAttachPayment>
                                  <WrapperImgSlip>
                                    <ImgSlip
                                      src={
                                        orderDetail?.payment?.paymentProof
                                          ?.docUrl
                                      }
                                      alt="paymentProof"
                                    />
                                  </WrapperImgSlip>

                                  <Button
                                    onClick={() => {
                                      window.open(
                                        `${orderDetail?.payment?.paymentProof?.docUrl}`,
                                        "_blank",
                                      );
                                    }}
                                    sx={{
                                      padding: "8px 16px",
                                      fontSize: font.size.sm,
                                      "&.MuiButton-outlined": {
                                        border: "1px solid #d9d9d9",
                                      },
                                    }}
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                  >
                                    {getLocale() === "th"
                                      ? "ดาวน์โหลด"
                                      : "Download"}
                                  </Button>
                                </WrapperAttachPayment>
                              </RowDownloadDoc>
                            </Grid>
                          </Grid>
                        </>
                      )}
                  </Grid>
                </DivBackground>
              </Grid>
            )}

            {/* Note */}
            <Grid item xs={12}>
              <DivBackground>
                <SpanTitle>{getLocale() === "th" ? "โน๊ต" : "Note"}</SpanTitle>

                <WrapperTextNote>
                  <TextField
                    id="internal-note-input"
                    placeholder={getLocale() === "th" ? "โน๊ต" : "Note"}
                    fullWidth
                    multiline
                    rows={6}
                    onChange={(e) => {
                      setUpdateOrder({
                        ...updateOrder,
                        internalNote: e.target.value,
                      });
                    }}
                    value={updateOrder?.internalNote}
                  />
                </WrapperTextNote>

                {checkAllowPermission("orders.can_update_cancelOrder") &&
                updateOrder?.internalNote !== orderDetail?.internalNote ? (
                  <Stack alignItems="flex-end" sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        updateInternalNote();
                      }}
                      sx={{ minWidth: 120 }}
                    >
                      {getLocale() === "th" ? "บันทึก" : "Save"}
                    </Button>
                  </Stack>
                ) : null}
              </DivBackground>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
}

export default CancelDetail;
